import Store from 'store/store';
import { accessNested } from 'utils/utils';
import { getBoundingRegions } from 'utils/bolt/showtimes';

import * as showtimesUtils from 'utils/bolt/showtimes';
import { arrayUnion, arraysEqual } from 'utils/utils';

const shallowEqual = (a, b) => a && b && arrayUnion(Object.keys(a), Object.keys(b)).every(k => a[k] === b[k]);


const getPageData = () => {
	let store = Store.get();
	let activePage = store.activePage;
	const pageType = store.appData?.pages?.[activePage]?.type;
	const pageId = (pageType === 'showtimes' || pageType === 'filmPage') ? activePage : 'boltShowtimes';
	let pageData = store.appData?.pages?.[pageId]?.data || {};
	if (pageType === 'filmPage') {
		pageData = Object.values(store.appData?.pages?.[activePage]?.data).find(section => section.type === 'showtimes')?.data || {};
	}
	return pageData;
};

Store.on('USER_LOCATION_UPDATED', (options = {}) => {
	let userCountry = (accessNested(Store.get(), 'client.geo.countryCode') || '').toUpperCase();
	let boundingRegions = getBoundingRegions();
	if (!boundingRegions || boundingRegions.includes(userCountry)) {
		Store.get().showtimes.set({ loading: true });
		if (getPageData().useV2) {
			Store.emit('SHOWTIMES_REFRESH');
		} else {
			Store.emit('FILTERS_REFRESH', { forceShowtimesRefresh: true });
		}
	}
});

Store.on('SHOWTIMES_REFRESH', async (options = {}) => {
	await showtimesUtils.refreshShowtimes(options);

	if (options.onLoad) {
		options.onLoad();
	} else {
		Store.get().showtimes.set({ loading: false });
		if (!getPageData().useV2) {
			Store.emit('GET_NEARBY_SHOWTIMES_LOCATIONS');
		}
	}
});

Store.on('UPDATE_SHOWTIMES_ACTIVE_TAB', (selectedTab, updateShowtimes) => {
	Store.get().showtimes.set({ groupBookingsTabActive: selectedTab === 'Group Bookings' });
	if (updateShowtimes) {
		Store.emit('SHOWTIMES_REFRESH');
	}
});

Store.on('GET_NEARBY_CINEMAS_FROM_CHAIN', async (theater) => {
	const options = {
		theater_id: theater.id,
		lat: theater.lat,
		lon: theater.lon
	};

	let showtimesState = Store.get().showtimes.nearby.chain;
	showtimesState.set({ loading: true, error: false, theater: theater.id });

	let apiResponse;
	try {
		apiResponse = await showtimesUtils.getNearbyCinemas(options);
	} catch (err) {
		let error = err?.message || err;
		Store.get().showtimes.nearby.chain.set({ error, loading: false });
		return;
	}

	let data = apiResponse.response;
	let more = accessNested(apiResponse.response, 'meta.more', false);
	data.forEach(newTheater => {
		newTheater.chain = theater.chain;
		showtimesUtils.processScreenings(newTheater);
	});

	Store.get().showtimes.nearby.chain.set({
		data,
		loading: false,
		more
	});
});

Store.on('GET_NEARBY_SHOWTIMES_LOCATIONS', async () => {
	let showtimesState = Store.get().showtimes.nearby.locations;
	showtimesState.set({ loading: true, error: false });

	let apiResponse;
	try {
		apiResponse = await showtimesUtils.getNearbyShowtimesLocations();
	} catch (err) {
		let error = (err && err.message) || err;
		Store.get().showtimes.nearby.locations.set({ error, loading: false });
		return;
	}

	let data = apiResponse.response;
	let more = accessNested(apiResponse.response, 'meta.more', false);

	Store.get().showtimes.nearby.locations.set({
		data,
		loading: false,
		more
	});
});

Store.on('SHOWTIMES_SHOW_MORE', async (options = {}) => {
	Store.get().showtimes.set({ loadingMore: true, error: false });
	await showtimesUtils.getMore(options);
	Store.get().showtimes.set({ loadingMore: false });
});

Store.on('FILTERS_REFRESH', async (options = {}) => {
	const filtersState = Store.get().showtimes.filters;
	filtersState.set({ loading: true }).now();

	let selectedChanged = await showtimesUtils.refreshFilters(options);
	if (typeof options.callback === 'function') {
		options.callback();
	} else if (selectedChanged || options.forceShowtimesRefresh) {
		Store.emit('SHOWTIMES_REFRESH', options);
	} else if (options.onNoSelectedFiltersChange) {
		options.onNoSelectedFiltersChange();
	}
});

Store.on('GET_THEATERS_AVAILABLE_DATES', async (theaterId) => {
	await showtimesUtils.fetchTheatersDates({
		api: {
			theaters_included: theaterId,
		}
	});
});

Store.on('FILTER_UPDATED', (...args) => {
	Store.emit('SHOWTIMES_REFRESH');
});

Store.on('FILTERS_RESET', () => {
	let filters = Store.get().showtimes.filters;
	if (!filters || !filters.selected) {
		return;
	}
	let selected = filters.selected;
	let available = filters.available || {};
	Object.keys(selected).forEach(filter => {
		let value = [];

		if (filter === 'date') {
			let today = new Date().toISOString().slice(0, 10);
			if (available.date && available.date.length) {
				let dates = filters.available.date;
				dates = dates.filter(d => d >= today).sort();
				value.push(dates[0]);
			} else {
				value.push(today);
			}
		}
		selected = selected.set(filter, value);
	});
	Store.emit('FILTER_UPDATED', '*');
});

Store.on('FILTER_RESET_SINGLE', (selectedFilter) => {
	let filters = Store.get().showtimes.filters;

	if (!filters || !filters.selected || !selectedFilter) {
		return;
	}

	filters.selected.set(selectedFilter, []);
	Store.emit('FILTER_UPDATED', selectedFilter, []);
});

// removeIfAlreadySet = toggle if the entire filter is exactly the same as the one we're trying to set
// e.g. FILTER_SET with option ['a', 'b'] would use ['a', 'b'] if the existing filter was just ['b']
//   but it would use [] (empty) is the existing filter was ['a', 'b'] or ['b', 'a']
Store.on('FILTER_SET', (filter, option, removeIfAlreadySet) => {
	let filters = Store.get().showtimes.filters;
	if (!filters?.selected) {
		return;
	}
	let data = Array.isArray(option) ? option : [option];
	if (removeIfAlreadySet) {
		let existing = filters.selected[filter] || [];
		if (data.length === existing.length && arraysEqual([...data].sort(), [...existing].sort())) {
			data = [];
		}
	}
	filters.selected.set(filter, data);
	if (filter === 'date') {
		Store.get().showtimes.filters.set('userSelectedDate', true);
	}
	Store.emit('FILTER_UPDATED', filter, data);
});
Store.on('FILTER_RESET', filter => Store.emit('FILTER_SET', filter, []));

Store.on('REVERT_FILTERS', (filters) => {
	if (!filters) {
		return;
	}
	const newFilters = Object.assign({}, { date: [], time: [], format: [] }, filters);
	Store.get().showtimes.filters.selected.set(newFilters);
	Store.emit('FILTER_UPDATED', '*');
});

function escapeRegExp(string) {
	return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}
Store.on('FILTER_TOGGLE', (filter, options, present) => {
	let filters = Store.get().showtimes.filters;
	if (!filters || !filters.selected) {
		return;
	}

	let list = filters.selected[filter] ? filters.selected[filter].toJS() : [];
	if (!Array.isArray(options)) {
		options = [options];
	}
	if (present == undefined) {
		// present not specified: toggle (add if not already there, remove if there)
		let all = showtimesUtils.expandFilterSelection(list, filters.available?.[filter]);
		if (filter === 'combinedLanguage') {
			present = !options.some(opt => all.find(ele => shallowEqual(ele, opt)));
		} else {
			present = !options.some(opt => all.includes(opt));
		}
	}
	if (filter === 'combinedLanguage') {
		list = list.filter(e => !options.find(opt => shallowEqual(opt, e)));
	} else {
		list = list.filter(e => !options.includes(e));
	}
	if (present) {
		list.push(...options);
	} else {
		// remove globs no longer matching
		list = list.filter(e => {
			if (filter === 'combinedLanguage') {
				return !options.find(opt => shallowEqual(opt, e));
			}

			if (!e.includes('*')) {
				return true;
			}
			let regex = new RegExp('^' + e.split('*').map(escapeRegExp).join('(.*)') + '$', 'i');
			return !options.some(option => regex.test(option));
		});
	}
	filters.selected.set(filter, list);
	Store.emit('FILTER_UPDATED', filter, list);
});
Store.on('FILTER_ADD', (filter, option) => Store.emit('FILTER_TOGGLE', filter, option, true));
Store.on('FILTER_REMOVE', (filter, option) => Store.emit('FILTER_TOGGLE', filter, option, false));

Store.on('OPEN_PROVIDER_INTERSTITIAL', (data) => {
	Store.get().showtimes.set({
		providerInterstitialOpen: true,
		selectedScreening: data.screening,
		selectedTheater: data.theater
	});

	Store.emit('TOGGLE_INTERSTITIAL_OPEN', true);
});

Store.on('CLOSE_MOVIES_DROPDOWN', (value) => {
	Store.get().showtimes.set({ movieFilterClosed: value });
});

Store.on('CLOSE_PROVIDER_INTERSTITIAL', () => {
	Store.get().showtimes.set({
		providerInterstitialOpen: false,
		selectedScreening: {},
		selectedTheater: {}
	});

	Store.emit('TOGGLE_INTERSTITIAL_OPEN', false);
});


Store.on('SET_SHOWTIMES_BTN_ACTIONS', (data) => {
	if (!data || !data.action) {
		console.error('Trying to set the showtimes button action without a function');
		return;
	}
	Store.get().showtimes.actions.push(data);
});

// Store.on('SET_MOVIE_FILTER_ID', (id) => {
// 	Store.get().showtimes.set({ movieFilterId: id });
// });

Store.on('SET_FILTERS_SELECTED_MOVIE_ID', (id) => {
	Store.get().showtimes.filters.selected.set({ movie: id || [] });
});

Store.on('POP_SHOWTIMES_BTN_ACTION', () => {
	Store.get().showtimes?.actions?.pop();
});

Store.on('REMOVE_SHOWTIMES_BTN_ACTIONS', (tag) => {
	let showtimesState = Store.get().showtimes;
	let remaining = showtimesState.actions.filter(action => action.tag !== tag);
	showtimesState.set({ actions: remaining });
});

Store.on('IN_CINEMA_DETAIL_VIEW', (theater) => {
	let showtimesState = Store.get().showtimes;
	showtimesState.set({ inCinemaDetailView: theater?.id });
});

Store.on('EXIT_CINEMA_DETAIL_VIEW', () => {
	let showtimesState = Store.get().showtimes;
	showtimesState.set({ inCinemaDetailView: false });
});

Store.on('SHOWTIMES_MESSAGE_PLAYLIST_LOADED', (videos) => {
	let showtimesState = Store.get().showtimes;
	showtimesState.set({
		videos,
		videosLoading: false,
		activeVideo: videos[0]
	});
});

Store.on('SHOWTIMES_MESSAGE_VIDEOS_LOADING', (value) => {
	let showtimesState = Store.get().showtimes;
	showtimesState.set({ videosLoading: value });
});

Store.on('SHOWTIMES_MESSAGE_UPDATE_VIDEO', (activeVideo) => {
	let showtimesState = Store.get().showtimes;
	showtimesState.set({ activeVideo });
});
